import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles } from '@/utils/pageBranding'
import {
    User,
    ArrowLeft,
    Edit,
    Mail,
    Phone,
    Building,
    Shield,
    Calendar,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2,
    Users,
    Globe,
    RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { adminService, AdminUserDetailResponse } from '@/services/adminService'
import { formatDistanceToNow } from 'date-fns'

export default function AdminUserDetail() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [user, setUser] = useState<AdminUserDetailResponse['user'] | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [actionLoading, setActionLoading] = useState<string | null>(null)

    useEffect(() => {
        if (id) {
            fetchUser()
        }
    }, [id])

    const fetchUser = async () => {
        if (!id) return

        setLoading(true)
        setError('')
        try {
            const response = await adminService.getUserById(parseInt(id))

            if (response && response.success) {
                setUser(response.user)
            } else {
                setError(response?.message || 'Failed to fetch user')
            }
        } catch (err: any) {
            setError(adminService.handleError(err))
        } finally {
            setLoading(false)
        }
    }

    const handleUserAction = async (action: 'activate' | 'deactivate' | 'resend_welcome') => {
        if (!user) return

        setActionLoading(action)
        try {
            let response
            switch (action) {
                case 'activate':
                    response = await adminService.activateUser(user.id)
                    break
                case 'deactivate':
                    response = await adminService.deactivateUser(user.id)
                    break
                case 'resend_welcome':
                    response = await adminService.resendWelcomeEmail(user.id)
                    break
            }

            if (response.success) {
                // Refresh user data
                await fetchUser()
            } else {
                // Show the message from the service (which now explains the limitation)
                setError(response.message || 'Action failed')
            }
        } catch (err: any) {
            setError(adminService.handleError(err))
        } finally {
            setActionLoading(null)
        }
    }

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'admin':
            case 'superuser':
                return <Shield className="w-5 h-5" />
            case 'merchant_admin':
            case 'merchant_user':
                return <Building className="w-5 h-5" />
            default:
                return <Users className="w-5 h-5" />
        }
    }

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'admin':
            case 'superuser':
                return 'bg-red-100 text-red-800 border-red-200'
            case 'merchant_admin':
                return 'bg-blue-100 text-blue-800 border-blue-200'
            case 'merchant_user':
                return 'bg-blue-50 text-blue-700 border-blue-100'
            case 'submerchant_admin':
                return 'bg-green-100 text-green-800 border-green-200'
            case 'submerchant_user':
                return 'bg-green-50 text-green-700 border-green-100'
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200'
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading user details...</p>
                </div>
            </div>
        )
    }

    if (error || !user) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-white mb-2">Error</h2>
                    <p className="text-gray-300 mb-4">{error || 'User not found'}</p>
                    <Button
                        onClick={() => navigate('/admin/users')}
                        className="px-6 py-3 text-white bg-orange-500 hover:bg-orange-600"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Users
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-900">
            <div className="relative z-10 p-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/admin/users')}
                                className="p-2 text-white rounded-lg hover:bg-gray-800"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <div>
                                <div className="flex items-center space-x-3 mb-2">
                                    <div className="p-2 bg-blue-500/20 rounded-lg">
                                        <User className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <h1 className="text-4xl font-bold text-white">User Details</h1>
                                </div>
                                <p className="text-lg text-gray-300">View and manage user information</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Button
                                onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                                className="px-6 py-3 text-white bg-orange-500 hover:bg-orange-600"
                            >
                                <Edit className="w-5 h-5 mr-2" />
                                Edit User
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <p className="text-red-300">{error}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* User Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-6">Basic Information</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Full Name</label>
                                    <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold">
                                            {user.first_name[0]}{user.last_name[0]}
                                        </div>
                                        <div>
                                            <p className="text-lg font-semibold text-white">{user.first_name} {user.last_name}</p>
                                            <p className="text-sm text-gray-400">{user.email}</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Role</label>
                                    <div className="flex items-center space-x-2">
                                        {getRoleIcon(user.role)}
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getRoleColor(user.role)}`}>
                                            {user.role_display}
                                        </span>
                                    </div>
                                </div>

                                {user.company_name && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">Company</label>
                                        <div className="flex items-center space-x-2">
                                            <Building className="w-4 h-4 text-gray-400" />
                                            <span className="text-white">{user.company_name}</span>
                                        </div>
                                    </div>
                                )}

                                {user.phone_number && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">Phone</label>
                                        <div className="flex items-center space-x-2">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span className="text-white">{user.phone_number}</span>
                                        </div>
                                    </div>
                                )}

                                {user.country && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">Country</label>
                                        <div className="flex items-center space-x-2">
                                            <Globe className="w-4 h-4 text-gray-400" />
                                            <span className="text-white">{user.country.name}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* KYC Verification Status */}
                        <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-6">KYC Verification</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">KYC Status</label>
                                    <div className="flex items-center space-x-2">
                                        {user.is_kyc_verified ? (
                                            <CheckCircle className="w-5 h-5 text-green-400" />
                                        ) : (
                                            <XCircle className="w-5 h-5 text-yellow-400" />
                                        )}
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.is_kyc_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {user.is_kyc_verified ? 'Verified' : 'Unverified'}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Verification Level</label>
                                    <span className="text-white">{user.kyc_verification_level}</span>
                                </div>

                                {user.kyc_verification_date && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-400 mb-2">Verification Date</label>
                                        <div className="flex items-center space-x-2">
                                            <Calendar className="w-4 h-4 text-gray-400" />
                                            <span className="text-white">
                                                {formatDistanceToNow(new Date(user.kyc_verification_date), { addSuffix: true })}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Account Approval</label>
                                    <div className="flex items-center space-x-2">
                                        {user.is_account_approved ? (
                                            <CheckCircle className="w-5 h-5 text-green-400" />
                                        ) : (
                                            <XCircle className="w-5 h-5 text-red-400" />
                                        )}
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.is_account_approved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                            {user.is_account_approved ? 'Approved' : 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {user.kyc_verification_notes && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Verification Notes</label>
                                    <div className="bg-gray-800 p-3 rounded-lg">
                                        <p className="text-gray-300">{user.kyc_verification_notes}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Account Settings */}
                        <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-6">Account Settings</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Environment</label>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.environment === 'production' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {user.environment}
                                    </span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Currency</label>
                                    <span className="text-white">{user.currency}</span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Language</label>
                                    <span className="text-white">{user.language}</span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Theme</label>
                                    <span className="text-white capitalize">{user.theme}</span>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Brand Color</label>
                                    <div className="flex items-center space-x-2">
                                        <div
                                            className="w-6 h-6 rounded border border-gray-600"
                                            style={{ backgroundColor: user.brand_color }}
                                        />
                                        <span className="text-white font-mono">{user.brand_color}</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Brand Name</label>
                                    <span className="text-white">{user.name}</span>
                                </div>
                            </div>

                            {user.address && (
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Address</label>
                                    <div className="bg-gray-800 p-3 rounded-lg">
                                        <p className="text-gray-300">{user.address}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Merchant/Submerchant Information */}
                        {(user.merchant || user.submerchant) && (
                            <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                                <h2 className="text-xl font-semibold text-white mb-6">
                                    {user.merchant ? 'Merchant Information' : 'Submerchant Information'}
                                </h2>

                                {user.merchant ? (
                                    <div className="space-y-6">
                                        {/* Basic Merchant Info */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Company Name</label>
                                                <div className="flex items-center space-x-2">
                                                    <Building className="w-4 h-4 text-blue-400" />
                                                    <span className="text-white">{user.merchant.company_name}</span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                                                <div className="flex items-center space-x-2">
                                                    <Mail className="w-4 h-4 text-gray-400" />
                                                    <span className="text-white">{user.merchant.email}</span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.merchant.status === 'enabled' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {user.merchant.status}
                                                </span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Verification</label>
                                                <div className="flex items-center space-x-2">
                                                    {user.merchant.is_verified ? (
                                                        <CheckCircle className="w-4 h-4 text-green-400" />
                                                    ) : (
                                                        <XCircle className="w-4 h-4 text-yellow-400" />
                                                    )}
                                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.merchant.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {user.merchant.is_verified ? 'Verified' : 'Unverified'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Currency</label>
                                                <span className="text-white">{user.merchant.currency}</span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Commission Rate</label>
                                                <span className="text-white">{user.merchant.commission_rate}%</span>
                                            </div>
                                        </div>

                                        {/* Website and URLs */}
                                        {(user.merchant.website_url || user.merchant.webhook_url) && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {user.merchant.website_url && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Website</label>
                                                        <div className="flex items-center space-x-2">
                                                            <Globe className="w-4 h-4 text-gray-400" />
                                                            <a
                                                                href={user.merchant.website_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-400 hover:text-blue-300 truncate"
                                                            >
                                                                {user.merchant.website_url}
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}

                                                {user.merchant.webhook_url && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Webhook URL</label>
                                                        <div className="flex items-center space-x-2">
                                                            <Globe className="w-4 h-4 text-gray-400" />
                                                            <a
                                                                href={user.merchant.webhook_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-400 hover:text-blue-300 truncate"
                                                            >
                                                                {user.merchant.webhook_url}
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Crypto Payout Settings */}
                                        {user.merchant.payout_crypto_enabled && (
                                            <div className="bg-gray-800/50 rounded-lg p-4">
                                                <h3 className="text-lg font-medium text-white mb-3">Crypto Payout Settings</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Wallet Address</label>
                                                        <span className="text-white font-mono text-sm break-all">{user.merchant.payout_wallet_address}</span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Crypto Type</label>
                                                        <span className="text-white">{user.merchant.payout_crypto_type}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Statistics */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-blue-400">{user.merchant.users_count}</div>
                                                <div className="text-sm text-gray-400">Users</div>
                                            </div>
                                            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-green-400">{user.merchant.submerchants_count}</div>
                                                <div className="text-sm text-gray-400">Submerchants</div>
                                            </div>
                                            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-orange-400">{user.sale_shops_count}</div>
                                                <div className="text-sm text-gray-400">Sale Shops</div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {user.merchant.description && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                                                <div className="bg-gray-800 p-3 rounded-lg">
                                                    <p className="text-gray-300">{user.merchant.description}</p>
                                                </div>
                                            </div>
                                        )}

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-400 mb-2">Created</label>
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                <span className="text-white">
                                                    {formatDistanceToNow(new Date(user.merchant.created_at), { addSuffix: true })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : user.submerchant ? (
                                    <div className="space-y-6">
                                        {/* Basic Submerchant Info */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Name</label>
                                                <div className="flex items-center space-x-2">
                                                    <Users className="w-4 h-4 text-green-400" />
                                                    <span className="text-white">{user.submerchant.name}</span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                                                <div className="flex items-center space-x-2">
                                                    <Mail className="w-4 h-4 text-gray-400" />
                                                    <span className="text-white">{user.submerchant.email}</span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.submerchant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {user.submerchant.status}
                                                </span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Verification</label>
                                                <div className="flex items-center space-x-2">
                                                    {user.submerchant.is_verified ? (
                                                        <CheckCircle className="w-4 h-4 text-green-400" />
                                                    ) : (
                                                        <XCircle className="w-4 h-4 text-yellow-400" />
                                                    )}
                                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.submerchant.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {user.submerchant.is_verified ? 'Verified' : 'Unverified'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Currency</label>
                                                <span className="text-white">{user.submerchant.currency}</span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Commission Rate</label>
                                                <span className="text-white">{user.submerchant.commission_rate}%</span>
                                            </div>
                                        </div>

                                        {/* Parent Merchant */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-2">Parent Merchant</label>
                                            <div className="flex items-center space-x-2">
                                                <Building className="w-4 h-4 text-blue-400" />
                                                <span className="text-white">{user.submerchant.merchant.company_name}</span>
                                            </div>
                                        </div>

                                        {/* Website and URLs */}
                                        {(user.submerchant.website_url || user.submerchant.webhook_url) && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {user.submerchant.website_url && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Website</label>
                                                        <div className="flex items-center space-x-2">
                                                            <Globe className="w-4 h-4 text-gray-400" />
                                                            <a
                                                                href={user.submerchant.website_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-400 hover:text-blue-300 truncate"
                                                            >
                                                                {user.submerchant.website_url}
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}

                                                {user.submerchant.webhook_url && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Webhook URL</label>
                                                        <div className="flex items-center space-x-2">
                                                            <Globe className="w-4 h-4 text-gray-400" />
                                                            <a
                                                                href={user.submerchant.webhook_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-400 hover:text-blue-300 truncate"
                                                            >
                                                                {user.submerchant.webhook_url}
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Crypto Payout Settings */}
                                        {user.submerchant.payout_crypto_enabled && (
                                            <div className="bg-gray-800/50 rounded-lg p-4">
                                                <h3 className="text-lg font-medium text-white mb-3">Crypto Payout Settings</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Wallet Address</label>
                                                        <span className="text-white font-mono text-sm break-all">{user.submerchant.payout_wallet_address}</span>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-400 mb-2">Crypto Type</label>
                                                        <span className="text-white">{user.submerchant.payout_crypto_type}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Statistics */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-green-400">{user.submerchant.users_count}</div>
                                                <div className="text-sm text-gray-400">Users</div>
                                            </div>
                                            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-orange-400">{user.sale_shops_count}</div>
                                                <div className="text-sm text-gray-400">Sale Shops</div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {user.submerchant.description && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                                                <div className="bg-gray-800 p-3 rounded-lg">
                                                    <p className="text-gray-300">{user.submerchant.description}</p>
                                                </div>
                                            </div>
                                        )}

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-400 mb-2">Created</label>
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                <span className="text-white">
                                                    {formatDistanceToNow(new Date(user.submerchant.created_at), { addSuffix: true })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>

                    {/* Actions Sidebar */}
                    <div className="space-y-6">
                        {/* Quick Actions */}
                        <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>

                            <div className="space-y-3">
                                <Button
                                    onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                                    className="w-full justify-start px-4 py-3 text-white bg-orange-500 hover:bg-orange-600"
                                >
                                    <Edit className="w-4 h-4 mr-3" />
                                    Edit User
                                </Button>

                                {!user.is_active ? (
                                    <Button
                                        onClick={() => handleUserAction('activate')}
                                        disabled={actionLoading === 'activate'}
                                        className="w-full justify-start px-4 py-3 text-white bg-green-500 hover:bg-green-600"
                                    >
                                        {actionLoading === 'activate' ? (
                                            <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                                        ) : (
                                            <CheckCircle className="w-4 h-4 mr-3" />
                                        )}
                                        Activate User
                                    </Button>
                                ) : (
                                    null
                                )}

                                <Button
                                    onClick={fetchUser}
                                    disabled={loading}
                                    className="w-full justify-start px-4 py-3 text-gray-300 border border-gray-600 hover:bg-gray-700"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-3 ${loading ? 'animate-spin' : ''}`} />
                                    Refresh Data
                                </Button>
                            </div>
                        </div>

                        {/* User Stats */}
                        <div className="bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">User Statistics</h3>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Status</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Verification</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {user.is_verified ? 'Verified' : 'Unverified'}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">KYC Status</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_kyc_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {user.is_kyc_verified ? 'Verified' : 'Unverified'}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Account Approval</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_account_approved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                        {user.is_account_approved ? 'Approved' : 'Pending'}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Environment</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.environment === 'production' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {user.environment}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Sale Shops</span>
                                    <span className="text-white font-semibold">{user.sale_shops_count}</span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400">Transactions</span>
                                    <span className="text-white font-semibold">{user.transactions_count}</span>
                                </div>

                                {user.merchant && (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-400">Merchant Users</span>
                                            <span className="text-white font-semibold">{user.merchant.users_count}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-400">Submerchants</span>
                                            <span className="text-white font-semibold">{user.merchant.submerchants_count}</span>
                                        </div>
                                    </>
                                )}

                                {user.submerchant && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-400">Submerchant Users</span>
                                        <span className="text-white font-semibold">{user.submerchant.users_count}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
